import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Toolbar,
  AppBar,
  Tooltip,
  Button,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as RankingsIcon,
  Analytics as AnalyticsIcon,
  Search as KeywordIcon,
  Speed as RankingOnDemandIcon,
  People as CompetitorIcon,
  AutoAwesome as GenerativeSearchIcon,
  Description as MetaDescriptionIcon,
  LocalFireDepartment as FireIcon,
  AccountTree as TreeIcon,
  Schema as SchemaIcon,
  Folder as FolderIcon,
  AutoAwesome as SparkleIcon,
  Museum as MuseumIcon,
} from '@mui/icons-material';

interface TopNavigationProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  notificationCount?: number;
  onNotificationClick?: () => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({ 
  activeSection, 
  onSectionChange,
  notificationCount = 0,
  onNotificationClick
}) => {
  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'rankings', label: 'Rankings', icon: <RankingsIcon /> },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 'keyword-research', label: 'Keyword Research', icon: <KeywordIcon /> },
    { id: 'content-projects', label: 'Content Projects', icon: <FolderIcon /> },
    { id: 'ranking-on-demand', label: 'Ranking on Demand', icon: <RankingOnDemandIcon /> },
    { id: 'generative-search', label: 'Generative Search', icon: <GenerativeSearchIcon /> },
    { id: 'paa-trees', label: 'PAA Trees', icon: <TreeIcon /> },
    { id: 'meta-descriptions', label: 'Meta Descriptions', icon: <MetaDescriptionIcon /> },
    { id: 'ai-roast', label: 'AI Roast', icon: <FireIcon /> },
    { id: 'schema-generator', label: 'Schema Generator', icon: <SchemaIcon /> },
    { id: 'dynamic-content', label: 'Dynamic Content', icon: <MuseumIcon /> },
    { id: 'competitors', label: 'Competitors', icon: <CompetitorIcon /> },
  ];

  return (
    <AppBar 
      position="static" 
      sx={{ 
        bgcolor: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px' }}>
        {/* Logo/Brand on the left */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#7b61ff' }}>
          🦄 Unicorn SEO
        </Typography>
        
        {/* Navigation items in the center */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5, 
          overflowX: 'auto',
          flex: 1,
          justifyContent: 'center',
          mx: 2,
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '2px',
          }
        }}>
          {navigationItems.map((item) => (
            <Tooltip key={item.id} title={item.label} arrow>
              <IconButton
                onClick={() => onSectionChange(item.id)}
                sx={{
                  color: activeSection === item.id ? '#7b61ff' : '#666',
                  bgcolor: activeSection === item.id ? 'rgba(123, 97, 255, 0.1)' : 'transparent',
                  borderRadius: '8px',
                  padding: '6px',
                  minWidth: '40px',
                  '&:hover': {
                    bgcolor: activeSection === item.id ? 'rgba(123, 97, 255, 0.15)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        
        {/* Sparkle icon on the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={notificationCount > 0 ? `${notificationCount} new updates` : 'What\'s new'} arrow>
            <IconButton
              onClick={onNotificationClick}
              sx={{
                color: '#7b61ff',
                bgcolor: notificationCount > 0 ? 'rgba(123, 97, 255, 0.1)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(123, 97, 255, 0.15)',
                },
                '@keyframes sparkle': {
                  '0%': { transform: 'scale(1) rotate(0deg)' },
                  '50%': { transform: 'scale(1.1) rotate(180deg)' },
                  '100%': { transform: 'scale(1) rotate(360deg)' }
                },
                animation: notificationCount > 0 ? 'sparkle 2s infinite' : 'none',
              }}
            >
              <Badge 
                badgeContent={notificationCount} 
                color="secondary"
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: '#ff4757',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }
                }}
              >
                <SparkleIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNavigation;